import { Box, Button, Stack, Tag } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import React, { type Dispatch, type ReactElement, type SetStateAction } from 'react'

import logger from '@/utils/logger'

import type { Query } from '@/client/query'

import { type FilterBy, useQueryFilterBy, useQueryTopics } from '@/components/Course/helpers'
import ModalV3 from '@/components/UI/Modal/ModalV3'

import CourseList from './CourseList'
import TopicFilter from './TopicFilter'
import TypeFilter from './TypeFilter'

// Passthrough parameters making me(Joy) sad
// But alas if we don't do this then only PART of the forms will be disabled
// and the other part will not (e.g. checkboxes and not tags)

// FUTURE improvement: give a "Page" a loading state that you can toggle on and off

export default function CourseFilters({
  isModal = false,
  disabled,
  setPageDisabled,
  course = null,
}: {
  isModal?: boolean
  disabled: boolean
  course?: Query | null
  setPageDisabled: Dispatch<SetStateAction<boolean>>
}): ReactElement {
  const router = useRouter()

  const [showFilterMobile, setShowFilterMobile] = React.useState(false)

  const querySelectedTopics = useQueryTopics()
  const querySelectedFilterBy = useQueryFilterBy()

  const [selectedStaging, setSelectedStaging] = React.useState<{
    topics: Set<number>
    type: FilterBy
  }>(() => ({
    topics: new Set(querySelectedTopics),
    type: null,
  }))

  const handleApplyFilters = React.useCallback(() => {
    setShowFilterMobile(false)
    router
      .replace({
        query: {
          ...router.query,
          topics: Array.from(selectedStaging.topics),
        },
      })
      .catch(err => {
        logger.error('Navigation failed', { err })
      })
  }, [router, selectedStaging.topics])

  if (isModal) {
    return (
      <Box display={['block', 'block', 'none']}>
        <Tag
          mr={1}
          mb={1}
          color="primary.solid"
          backgroundColor="bg"
          fontWeight="bold"
          onClick={() => setShowFilterMobile(true)}
          _hover={{
            cursor: 'pointer',
          }}
        >
          Filters
        </Tag>
        <ModalV3
          title={`Filter ${course == null ? 'Courses' : 'Topics'}`}
          open={showFilterMobile}
          onClose={() => setShowFilterMobile(false)}
          size="full"
          body={() => (
            <Stack direction="column" spacing="24px">
              {course == null ? (
                <CourseList />
              ) : (
                <TopicFilter
                  course={course}
                  isModal
                  selectedTopics={new Set(querySelectedTopics)}
                  selectedStaging={selectedStaging}
                  setSelectedStaging={setSelectedStaging}
                  disabled={disabled}
                  setPageDisabled={setPageDisabled}
                />
              )}
              <TypeFilter
                isModal
                selectedType={querySelectedFilterBy}
                selectedStaging={selectedStaging}
                setSelectedStaging={setSelectedStaging}
                disabled={disabled}
                setPageDisabled={setPageDisabled}
              />
            </Stack>
          )}
          rightFooterContent={() => (
            <Stack direction="row" spacing="8px">
              <Button variant="outline" onClick={() => setShowFilterMobile(false)}>
                Cancel
              </Button>
              <Button onClick={handleApplyFilters}>Apply</Button>
            </Stack>
          )}
        />
      </Box>
    )
  }
  return (
    <Stack direction="column" spacing="24px" position="sticky" top="64px" py={3}>
      {course == null ? (
        <CourseList />
      ) : (
        <TopicFilter
          course={course}
          selectedTopics={new Set(querySelectedTopics)}
          selectedStaging={selectedStaging}
          setSelectedStaging={setSelectedStaging}
          disabled={disabled}
          setPageDisabled={setPageDisabled}
        />
      )}
      <TypeFilter
        selectedType={querySelectedFilterBy}
        selectedStaging={selectedStaging}
        setSelectedStaging={setSelectedStaging}
        disabled={disabled}
        setPageDisabled={setPageDisabled}
      />
    </Stack>
  )
}
